import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {
  AuthStore,
  FeatureService,
  InstallationDetailsStore,
  InstallationFeaturesStore,
} from '@store';
import {
  AlternativeBivalentTemperatureCommands,
  Feature,
  FeatureActionFields,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
  HeatingSecondaryHeatGeneratorRuntimeCommands,
  HeatingSecondaryHeatGeneratorRuntimeProperties,
  IsNumberValueProperty,
  IsStringValueProperty,
  RangeConstraints,
  ScheduleCommands,
  ScheduleProperties,
  SecondaryHeatGeneratorCommands,
  SecondaryHeatGeneratorConfigurationOffsetCommand,
  SecondaryHeatGeneratorConfigurationTimeIntegralThresholdCommand,
  SetEnergyFactorCommand,
  SetTariffNumberCommand,
} from '@vi-iot/client/index';
import { Observable, takeUntil, tap, Subject, BehaviorSubject } from 'rxjs';
import { HybridProOperatingModesActivePipe } from '../pipes/hybrid-pro-operating-modes-active.pipe';
import { ItemComponentHelper } from '@vi-iot/ui/components/item/item.component.helper';
import {
  Program,
  ScheduleEntry,
  ScheduleService,
  TimemanagerDialogService,
} from 'src/app/features/timemanager/services';
import { MatDialog } from '@angular/material/dialog';
import { EditFeatureValueModalComponent } from 'src/app/shared/components/edit-feature-value-modal/edit-feature-value-modal.component';
import { ConstraintsPipe } from 'src/app/operational-data-common/feature-pipes/constraints.pipe';
import { EditFeatureValueData } from 'src/app/shared/models/editFeatureValueData.model';
import { EditFeatureModalHelper } from 'src/app/shared/components/edit-feature-value-modal/edit-feature-value-modal.helper';
import { HybridProPanelHelper } from '../hybrid-pro-panel.helper';
import { GTMService } from '@services';
import { Currency } from 'src/app/shared/common/currency';
import { SUFFIX } from 'src/app/shared/common/units';
import { MatTooltip } from '@angular/material/tooltip';
import { matTooltipSetter } from 'src/app/shared/functions/mat-tooltip-setter/mat-tooltip-setter';

@Component({
  selector: 'app-hybrid-pro-overlay',
  templateUrl: './hybrid-pro-overlay.component.html',
  styleUrl: './hybrid-pro-overlay.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HybridProOverlayComponent implements OnInit, OnDestroy {
  readonly featureCommands = FeatureCommands;
  readonly featureActionFields = FeatureActionFields;
  readonly featurePropertyKey = FeaturePropertyKey;

  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }

  controlStrategy$: Observable<Feature<IsStringValueProperty, SecondaryHeatGeneratorCommands>>;
  controlStrategyEnum: ItemComponentHelper.ItemOption[];
  controlModeEnum: ItemComponentHelper.ItemOption[] = [];
  heatGeneratorEnergyCosts$: Observable<Feature<IsNumberValueProperty, SetTariffNumberCommand>>;
  electricityNormalRate$: Observable<Feature<IsNumberValueProperty, SetTariffNumberCommand>>;
  electricityLowRate$: Observable<Feature<IsNumberValueProperty, SetTariffNumberCommand>>;
  primaryEnergyFactorExternalElectricityEnergyFactor$: Observable<
    Feature<IsNumberValueProperty, SetEnergyFactorCommand>
  >;
  primaryEnergyFactorExternalHeatGenerator$: Observable<
    Feature<IsNumberValueProperty, SetEnergyFactorCommand>
  >;
  electricityPriceTimeSchedule$: Observable<Feature<ScheduleProperties, ScheduleCommands>>;
  programs$: BehaviorSubject<Program[]> = new BehaviorSubject<Program[]>([]);

  currencyOfConsumption: string;
  controlMode$: Observable<Feature<IsStringValueProperty, SecondaryHeatGeneratorCommands>>;

  secondaryHeatGeneratorBivalencePoint$: Observable<
    Feature<IsNumberValueProperty, AlternativeBivalentTemperatureCommands>
  >;

  secondaryHeatGeneratorAlternativePoint$: Observable<
    Feature<IsNumberValueProperty, AlternativeBivalentTemperatureCommands>
  >;

  secondaryHeatGeneratorConfigurationRuntime$: Observable<
    Feature<
      HeatingSecondaryHeatGeneratorRuntimeProperties,
      HeatingSecondaryHeatGeneratorRuntimeCommands
    >
  >;
  secondaryHeatGeneratorConfigurationTimeIntegralThreshold$: Observable<
    Feature<IsNumberValueProperty, SecondaryHeatGeneratorConfigurationTimeIntegralThresholdCommand>
  >;
  secondaryHeatGeneratorDhwOffset$: Observable<
    Feature<IsNumberValueProperty, SecondaryHeatGeneratorConfigurationOffsetCommand>
  >;
  secondaryHeatGeneratorTemperatureOffset$: Observable<
    Feature<IsNumberValueProperty, SecondaryHeatGeneratorConfigurationOffsetCommand>
  >;
  temperatureValues = [];

  private ngUnsubscribe$: Subject<void> = new Subject();

  constructor(
    private installationFeaturesStore: InstallationFeaturesStore,
    private hybridProOperatingModesActivePipe: HybridProOperatingModesActivePipe,
    private scheduleService: ScheduleService,
    private timemanagerDialogService: TimemanagerDialogService,
    private featureService: FeatureService,
    private dialog: MatDialog,
    private constraintsPipe: ConstraintsPipe,
    private gtmService: GTMService,
    protected installationDetailsStore: InstallationDetailsStore,
    protected authStore: AuthStore
  ) {}

  ngOnInit(): void {
    this.programs$ = this.scheduleService.programs$;
    this.controlStrategy$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature<IsStringValueProperty, SecondaryHeatGeneratorCommands>(
        FeatureNames.secondaryHeatGeneratorOperatingModesActive
      )
      .pipe(
        tap(
          (feature) =>
            (this.controlStrategyEnum = feature?.commands?.setMode?.params?.mode?.constraints?.enum.map(
              (entry): ItemComponentHelper.ItemOption => ({
                value: entry,
                viewValue: this.hybridProOperatingModesActivePipe.transform(entry),
              })
            ))
        )
      );

    this.heatGeneratorEnergyCosts$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      IsNumberValueProperty,
      SetTariffNumberCommand
    >(FeatureNames.secondaryHeatGeneratorFossilPriceNormal);

    this.electricityNormalRate$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      IsNumberValueProperty,
      SetTariffNumberCommand
    >(FeatureNames.secondaryHeatGeneratorlElectricityPriceNormal);

    this.electricityLowRate$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      IsNumberValueProperty,
      SetTariffNumberCommand
    >(FeatureNames.secondaryHeatGeneratorlElectricityPriceLow);

    this.primaryEnergyFactorExternalElectricityEnergyFactor$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      IsNumberValueProperty,
      SetEnergyFactorCommand
    >(FeatureNames.secondaryHeatGeneratorElectricityEnergyFactor);

    this.primaryEnergyFactorExternalHeatGenerator$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      IsNumberValueProperty,
      SetEnergyFactorCommand
    >(FeatureNames.secondaryHeatGeneratorFossilEnergyFactor);

    this.electricityPriceTimeSchedule$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature<ScheduleProperties, ScheduleCommands>(
        FeatureNames.secondaryHeatGeneratorElecticityPriceSchedule
      )
      .pipe(
        tap((feature) => {
          this.scheduleService.createSchedule(feature);
          const modes = this.featureService.getScheduleModes(feature);
          this.scheduleService.createModes(modes);
          this.scheduleService.isETPSchedule$.next(true);
        })
      );

    this.installationDetailsStore.installationAddressInfo$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((address) => {
        if (address?.country) {
          const currency = Currency.getCurrencyBasingOnVkorg(address.country);

          if (currency) {
            this.currencyOfConsumption = currency
              ? `${currency}/${SUFFIX.kilowattHour}`
              : undefined;
          }
        }
      });
    this.controlMode$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature<IsStringValueProperty, SecondaryHeatGeneratorCommands>(
        FeatureNames.secondaryHeatGeneratorConfiguration
      )
      .pipe(
        takeUntil(this.ngUnsubscribe$),
        tap(
          (feature) =>
            (this.controlModeEnum = feature?.commands?.setMode?.params?.mode?.constraints?.enum.map(
              (mode: string): ItemComponentHelper.ItemOption => ({
                value: mode,
                viewValue: this.hybridProOperatingModesActivePipe.transform(mode),
              })
            ))
        )
      );

    this.secondaryHeatGeneratorBivalencePoint$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      IsNumberValueProperty,
      AlternativeBivalentTemperatureCommands
    >(FeatureNames.secondaryHeatGeneratorBivalencePoint);

    this.secondaryHeatGeneratorAlternativePoint$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      IsNumberValueProperty,
      AlternativeBivalentTemperatureCommands
    >(FeatureNames.secondaryHeatGeneratorAlternativePoint);

    this.secondaryHeatGeneratorConfigurationRuntime$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      HeatingSecondaryHeatGeneratorRuntimeProperties,
      HeatingSecondaryHeatGeneratorRuntimeCommands
    >(FeatureNames.secondaryHeatGeneratorConfigurationRuntime);

    this.secondaryHeatGeneratorConfigurationTimeIntegralThreshold$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      IsNumberValueProperty,
      SecondaryHeatGeneratorConfigurationTimeIntegralThresholdCommand
    >(FeatureNames.secondaryHeatGeneratorConfigurationTimeIntegralThreshold);

    this.secondaryHeatGeneratorTemperatureOffset$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      IsNumberValueProperty,
      SecondaryHeatGeneratorConfigurationOffsetCommand
    >(FeatureNames.secondaryHeatGeneratorTemperatureOffset);

    this.secondaryHeatGeneratorDhwOffset$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      IsNumberValueProperty,
      SecondaryHeatGeneratorConfigurationOffsetCommand
    >(FeatureNames.secondaryHeatGeneratorDhwOffset);
  }

  onEdit(
    feature: Feature<
      IsStringValueProperty | IsNumberValueProperty,
      | SetTariffNumberCommand
      | SetEnergyFactorCommand
      | SecondaryHeatGeneratorConfigurationTimeIntegralThresholdCommand
      | SecondaryHeatGeneratorConfigurationOffsetCommand
    >,
    featureCommand: string,
    featurePropertyKey: FeaturePropertyKey,
    showUnit?: boolean,
    commandParam?: string
  ): void {
    const constraints: RangeConstraints = this.constraintsPipe.transform(
      feature,
      featureCommand,
      commandParam || featurePropertyKey
    );

    const dialogData: EditFeatureValueData = {
      title: 'FEATURE-VALUE-EDIT-MODAL.MODAL_TITLE',
      categoryNameForGTM: 'Hybrid Pro',
      value: feature.properties?.value?.value,
      inputSettings: {
        name: this.getTranslationLabelForPrice(feature.feature),
        actionNameForGTM: feature.feature,
        type: 'number',
        isRequired: true,
        min: constraints.min,
        max: constraints.max,
        pattern: '^[0-9]+(\\.[0-9]+)?$',
        step: constraints.stepping,
        ...(showUnit &&
          this.currencyOfConsumption && {
            unit: this.currencyOfConsumption,
          }),
      },
      onSaveProperties: {
        featureName: feature.feature,
        propertyName: FeaturePropertyKey.value,
        paramName: commandParam || featurePropertyKey,
        commandName: feature.commands[featureCommand].name,
      },
      errorText: {
        ...EditFeatureModalHelper.errorText,
        step: 'FEATURE-VALUE-EDIT-MODAL.ERROR_MSG.STEP_MULTIPLE',
      },
    };

    this.dialog.open(EditFeatureValueModalComponent, {
      width: '496px',
      disableClose: true,
      panelClass: 'edit-feature-value-dialog',
      data: dialogData,
    });
  }

  onEditRuntime(
    feature: Feature<
      HeatingSecondaryHeatGeneratorRuntimeProperties,
      HeatingSecondaryHeatGeneratorRuntimeCommands
    >,
    featureCommand: string,
    featurePropertyKey: FeaturePropertyKey,
    commandParam: string,
    showUnit?: boolean
  ): void {
    const constraints: RangeConstraints = this.constraintsPipe.transform(
      feature,
      featureCommand,
      commandParam
    );

    const { minimal, delay, break: breakValue } = feature.properties;
    let translationLabel;
    if (featurePropertyKey === FeaturePropertyKey.minimal) {
      translationLabel = '+INSTALLATIONS.INSTALLATION.PANEL.HYBRID_PRO.MINIMAL_RUNTIME.TITLE';
    } else if (featurePropertyKey === FeaturePropertyKey.delay) {
      translationLabel = '+INSTALLATIONS.INSTALLATION.PANEL.HYBRID_PRO.START_DELAY.TITLE';
    } else if (featurePropertyKey === FeaturePropertyKey.break) {
      translationLabel = '+INSTALLATIONS.INSTALLATION.PANEL.HYBRID_PRO.SHUT_DOWN_DELAY.TITLE';
    } else {
      translationLabel = '';
    }

    const dialogData: EditFeatureValueData = {
      title: 'FEATURE-VALUE-EDIT-MODAL.MODAL_TITLE',
      categoryNameForGTM: 'Hybrid Pro',
      value: minimal?.value || delay?.value || breakValue?.value,
      inputSettings: {
        name: translationLabel,
        actionNameForGTM: feature.feature,
        type: 'number',
        isRequired: true,
        min: constraints.min,
        max: constraints.max,
        pattern: '^[0-9]+(\\.[0-9]+)?$',
        step: constraints.stepping,
        ...(showUnit &&
          this.currencyOfConsumption && {
            unit: this.currencyOfConsumption,
          }),
      },
      onSaveProperties: {
        featureName: feature.feature,
        propertyName: featurePropertyKey,
        paramName: commandParam,
        commandName: feature.commands[featureCommand].name,
      },
      errorText: {
        ...EditFeatureModalHelper.errorText,
        step: 'FEATURE-VALUE-EDIT-MODAL.ERROR_MSG.STEP_MULTIPLE',
      },
    };

    this.dialog.open(EditFeatureValueModalComponent, {
      width: '496px',
      disableClose: true,
      panelClass: 'edit-feature-value-dialog',
      data: dialogData,
    });
  }

  onEditOffsetValues(
    feature: Feature<IsNumberValueProperty, SecondaryHeatGeneratorConfigurationOffsetCommand>,
    featureCommand: string,
    featurePropertyKey: FeaturePropertyKey,
    showUnit?: boolean
  ): void {
    const constraints: RangeConstraints = this.constraintsPipe.transform(
      feature,
      featureCommand,
      FeatureActionFields.temperature
    );

    const dialogData: EditFeatureValueData = {
      title: 'FEATURE-VALUE-EDIT-MODAL.MODAL_TITLE',
      categoryNameForGTM: 'Hybrid Pro',
      value: feature?.properties?.value?.value,
      inputSettings: {
        name: this.getTranslationLabelForPrice(feature.feature),
        actionNameForGTM: feature.feature,
        type: 'number',
        isRequired: true,
        min: constraints.min,
        max: constraints.max,
        pattern: '^[0-9]+(\\.[0-9]+)?$',
        step: constraints.stepping,
        ...(showUnit &&
          this.currencyOfConsumption && {
            unit: this.currencyOfConsumption,
          }),
      },
      onSaveProperties: {
        featureName: feature.feature,
        propertyName: featurePropertyKey,
        paramName: this.featureActionFields.temperature,
        commandName: feature.commands[featureCommand].name,
      },
      errorText: {
        ...EditFeatureModalHelper.errorText,
        step: 'FEATURE-VALUE-EDIT-MODAL.ERROR_MSG.STEP_MULTIPLE',
      },
    };

    this.dialog.open(EditFeatureValueModalComponent, {
      width: '496px',
      disableClose: true,
      panelClass: 'edit-feature-value-dialog',
      data: dialogData,
    });
  }

  private getTranslationLabelForPrice(featureName: string): string {
    switch (featureName) {
      case FeatureNames.secondaryHeatGeneratorFossilPriceNormal:
        return '+INSTALLATIONS.INSTALLATION.PANEL.HYBRID_PRO.EXTERNAL_HEAT_GENERATOR_COSTS.TITLE';
      case FeatureNames.secondaryHeatGeneratorlElectricityPriceNormal:
        return '+INSTALLATIONS.INSTALLATION.PANEL.HYBRID_PRO.ELECTRICITY_NORMAL_RATE.TITLE';
      case FeatureNames.secondaryHeatGeneratorlElectricityPriceLow:
        return '+INSTALLATIONS.INSTALLATION.PANEL.HYBRID_PRO.ELECTRICITY_LOW_RATE.TITLE';
      case FeatureNames.secondaryHeatGeneratorElectricityEnergyFactor:
        return '+INSTALLATIONS.INSTALLATION.PANEL.HYBRID_PRO.PRIMARY_ENERGY_FACTOR_HEAT_PUMP.TITLE';
      case FeatureNames.secondaryHeatGeneratorFossilEnergyFactor:
        return '+INSTALLATIONS.INSTALLATION.PANEL.HYBRID_PRO.PRIMARY_ENERGY_EXTERNAL_FACTOR_HEAT_GENERATOR.TITLE';
      case FeatureNames.secondaryHeatGeneratorConfigurationTimeIntegralThreshold:
        return '+INSTALLATIONS.INSTALLATION.PANEL.HYBRID_PRO.INTEGRAL_THRESHOLD.TITLE';
      case FeatureNames.secondaryHeatGeneratorDhwOffset:
        return '+INSTALLATIONS.INSTALLATION.PANEL.HYBRID_PRO.OFFSET_DHW.TITLE';
      case FeatureNames.secondaryHeatGeneratorTemperatureOffset:
        return '+INSTALLATIONS.INSTALLATION.PANEL.HYBRID_PRO.TEMPERATURE_OFFSET.TITLE';
      default:
        return '';
    }
  }

  onControlStrategyChange(
    mode: HybridProPanelHelper.SecondaryHeatGeneratorOperatingModesActive
  ): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.secondaryHeatGeneratorOperatingModesActive,
      mode,
      FeaturePropertyKey.value,
      FeatureActionFields.mode,
      FeatureCommands.setMode
    );

    this.gtmService.track('onSet', 'Hybrid pricing', 'hybridPro');
  }

  onControlModeChange(mode: HybridProPanelHelper.SecondaryHeatGeneratorModes): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.secondaryHeatGeneratorConfiguration,
      mode,
      FeaturePropertyKey.value,
      FeatureActionFields.mode,
      FeatureCommands.setMode
    );

    this.gtmService.track('onSet', 'Hybrid control mode', 'hybridPro');
  }

  onDualModeChange(value: number): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.secondaryHeatGeneratorBivalencePoint,
      value,
      FeaturePropertyKey.temperature,
      FeatureActionFields.temperature,
      FeatureCommands.setTemperature
    );

    this.gtmService.track('onSet', 'set bivalence point temperature', 'hybridPro');
  }

  onAlternativePointChange(value: number): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.secondaryHeatGeneratorAlternativePoint,
      value,
      FeaturePropertyKey.temperature,
      FeatureActionFields.temperature,
      FeatureCommands.setTemperature
    );

    this.gtmService.track('onSet', 'set alternative point temperature', 'hybridPro');
  }

  onScheduleChange(
    event: { scheduleIds: number[]; periods: ScheduleEntry[] },
    feature: Feature<ScheduleProperties, ScheduleCommands>
  ): void {
    const schedule = this.scheduleService.getScheduleEntries(feature);
    const value = this.scheduleService.getScheduleValue(event.scheduleIds, event.periods, schedule);

    this.installationFeaturesStore.updateFeature(
      FeatureNames.secondaryHeatGeneratorElecticityPriceSchedule,
      structuredClone(value),
      FeaturePropertyKey.entries,
      FeatureActionFields.newSchedule,
      FeatureCommands.setSchedule
    );
  }

  onEditMultipleDays(): void {
    this.timemanagerDialogService.openSelectDaysDialog();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
